import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { AlertTriangle, Plus, Trash2 } from 'lucide-react'
import {
  api,
  ApiError,
  type FbPixelEntry,
  type PaS2sSettings,
  type TsS2sSettings,
} from '@/lib/api/client'
import { useAuth } from '@/lib/auth'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Switch } from '@/components/ui/switch'
import { Button } from '@/components/ui/button'
import { Dialog } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { CardSkeleton } from '@/components/common/skeleton'
import { ErrorState } from '@/components/common/error-state'

const EMPTY_PIXEL: FbPixelEntry = { pixel_id: '', label: '', access_token: '' }

export function SettingsPage() {
  const { isAdmin } = useAuth()
  const queryClient = useQueryClient()

  const [gateDialog, setGateDialog] = useState<{
    channel: string
    brandId: string
    enabled: boolean
    message: string
  } | null>(null)
  const [confirmLive, setConfirmLive] = useState(false)

  const [fbPixels, setFbPixels] = useState<FbPixelEntry[]>([])
  const [fbGraphVersion, setFbGraphVersion] = useState('v21.0')
  const [paS2s, setPaS2s] = useState<PaS2sSettings>({ postback_url: '', aid: '', tid: '' })
  const [tsS2s, setTsS2s] = useState<TsS2sSettings>({ postback_url: '', api_key: '', goal_id: '' })

  const settingsQuery = useQuery({
    queryKey: ['settings'],
    queryFn: () => api.getSettings(),
  })

  const brandsQuery = useQuery({
    queryKey: ['brands'],
    queryFn: () => api.getBrands(),
  })

  const brandId = brandsQuery.data?.brands[0]?.brand_id ?? 'default'
  const channelSettings = settingsQuery.data?.channel_settings?.[brandId]
  const showChannelConfig = Boolean(channelSettings) || isAdmin

  useEffect(() => {
    if (!channelSettings) return
    if (channelSettings.fb) {
      setFbGraphVersion(channelSettings.fb.graph_version ?? 'v21.0')
      setFbPixels(
        channelSettings.fb.pixels.length > 0
          ? channelSettings.fb.pixels.map((p) => ({ ...p }))
          : [{ ...EMPTY_PIXEL }],
      )
    }
    if (channelSettings.pa) {
      setPaS2s({
        postback_url: channelSettings.pa.postback_url ?? '',
        aid: channelSettings.pa.aid ?? '',
        tid: channelSettings.pa.tid ?? '',
      })
    }
    if (channelSettings.ts) {
      setTsS2s({
        postback_url: channelSettings.ts.postback_url ?? '',
        api_key: channelSettings.ts.api_key ?? '',
        goal_id: channelSettings.ts.goal_id ?? '',
      })
    }
  }, [channelSettings])

  const patchMutation = useMutation({
    mutationFn: ({
      brandId,
      channel,
      enabled,
      confirmLive,
    }: {
      brandId: string
      channel: string
      enabled: boolean
      confirmLive?: boolean
    }) => api.patchChannel(brandId, channel, enabled, confirmLive),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings'] })
      queryClient.invalidateQueries({ queryKey: ['brand-config'] })
      setGateDialog(null)
      setConfirmLive(false)
    },
    onError: (err, vars) => {
      if (err instanceof ApiError && err.status === 409) {
        const message =
          typeof err.body === 'object' && err.body !== null && 'detail' in err.body
            ? String((err.body as { detail: unknown }).detail)
            : 'Live dispatch is blocked by the dry-run safety gate.'
        setGateDialog({
          channel: vars.channel,
          brandId: vars.brandId,
          enabled: vars.enabled,
          message,
        })
      }
    },
  })

  const fbPixelsMutation = useMutation({
    mutationFn: () =>
      api.patchFbPixels(brandId, {
        graph_version: fbGraphVersion,
        pixels: fbPixels.filter((p) => p.pixel_id.trim()),
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['settings'] }),
  })

  const paS2sMutation = useMutation({
    mutationFn: () =>
      api.patchPaS2s(brandId, {
        postback_url: paS2s.postback_url,
        aid: paS2s.aid,
        tid: paS2s.tid,
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['settings'] }),
  })

  const tsS2sMutation = useMutation({
    mutationFn: () =>
      api.patchTsS2s(brandId, {
        postback_url: tsS2s.postback_url,
        api_key: tsS2s.api_key,
        goal_id: tsS2s.goal_id,
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['settings'] }),
  })

  if (settingsQuery.isLoading) {
    return (
      <div className="space-y-6">
        <CardSkeleton />
        <CardSkeleton />
      </div>
    )
  }

  if (settingsQuery.isError || !settingsQuery.data) {
    return (
      <ErrorState
        message={settingsQuery.error?.message ?? 'Failed to load settings'}
        onRetry={() => settingsQuery.refetch()}
      />
    )
  }

  const settings = settingsQuery.data

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-semibold tracking-tight">Settings</h2>
        <p className="text-sm text-muted-foreground">
          Deployment configuration, channel controls, and postback endpoints
        </p>
      </div>

      {(settings.dry_run || settings.banner) && (
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="flex items-start gap-3 py-4">
            <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" />
            <div className="text-sm text-amber-900">
              {settings.banner ?? (
                <>
                  <strong>DRY RUN mode is active.</strong> Dispatches are simulated and not sent
                  to live ad platforms.
                </>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Deployment</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-3">
            <span className="text-sm text-muted-foreground">Deployment ID</span>
            <code className="rounded bg-muted px-2 py-1 font-mono text-sm">
              {settings.deployment_id}
            </code>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm text-muted-foreground">Dry run</span>
            <Badge variant={settings.dry_run ? 'warning' : 'success'}>
              {settings.dry_run ? 'Enabled' : 'Disabled'}
            </Badge>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Channel dispatch</CardTitle>
        </CardHeader>
        <CardContent>
          {!isAdmin ? (
            <p className="text-sm text-muted-foreground">
              Channel toggles are available to admin users only. You are signed in as a viewer.
            </p>
          ) : (
            <ul className="divide-y">
              {Object.entries(settings.enabled_channels).map(([channel, enabled]) => (
                <li key={channel} className="flex items-center justify-between py-4">
                  <div>
                    <p className="font-medium capitalize">{channel.replace(/^[^:]+:/, '')}</p>
                    <p className="text-xs text-muted-foreground">
                      {enabled ? 'Live dispatch enabled' : 'Dispatch disabled'}
                    </p>
                  </div>
                  <Switch
                    checked={enabled}
                    disabled={patchMutation.isPending}
                    onCheckedChange={(next) => {
                      const ch = channel.includes(':') ? channel.split(':')[1] : channel
                      patchMutation.mutate({ brandId, channel: ch, enabled: next })
                    }}
                  />
                </li>
              ))}
            </ul>
          )}
          {patchMutation.isError &&
            !(patchMutation.error instanceof ApiError && patchMutation.error.status === 409) && (
              <p className="mt-4 text-sm text-destructive">{patchMutation.error.message}</p>
            )}
        </CardContent>
      </Card>

      {showChannelConfig && (
        <Card>
          <CardHeader>
            <CardTitle>Facebook pixels</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {!isAdmin ? (
              channelSettings?.fb?.pixels?.length ? (
                <ul className="space-y-2 text-sm">
                  {channelSettings.fb.pixels.map((p) => (
                    <li key={p.pixel_id} className="font-mono">
                      {p.label ? `${p.label}: ` : ''}
                      {p.pixel_id}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-sm text-muted-foreground">No pixels configured.</p>
              )
            ) : (
              <>
                <div className="space-y-2">
                  <label className="text-sm text-muted-foreground">Graph API version</label>
                  <Input
                    value={fbGraphVersion}
                    onChange={(e) => setFbGraphVersion(e.target.value)}
                    placeholder="v21.0"
                  />
                </div>
                {fbPixels.map((pixel, idx) => (
                  <div key={idx} className="grid gap-2 rounded-md border p-3 sm:grid-cols-3">
                    <Input
                      value={pixel.label ?? ''}
                      onChange={(e) => {
                        const next = [...fbPixels]
                        next[idx] = { ...next[idx], label: e.target.value }
                        setFbPixels(next)
                      }}
                      placeholder="Label"
                    />
                    <Input
                      value={pixel.pixel_id}
                      onChange={(e) => {
                        const next = [...fbPixels]
                        next[idx] = { ...next[idx], pixel_id: e.target.value }
                        setFbPixels(next)
                      }}
                      placeholder="Pixel ID"
                    />
                    <div className="flex gap-2">
                      <Input
                        type="password"
                        value={pixel.access_token === '***' ? '' : (pixel.access_token ?? '')}
                        onChange={(e) => {
                          const next = [...fbPixels]
                          next[idx] = { ...next[idx], access_token: e.target.value }
                          setFbPixels(next)
                        }}
                        placeholder="Access token"
                        className="flex-1"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        aria-label="Remove pixel"
                        disabled={fbPixels.length <= 1}
                        onClick={() => setFbPixels(fbPixels.filter((_, i) => i !== idx))}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
                <div className="flex flex-wrap gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setFbPixels([...fbPixels, { ...EMPTY_PIXEL }])}
                  >
                    <Plus className="mr-2 h-4 w-4" />
                    Add pixel
                  </Button>
                  <Button
                    type="button"
                    disabled={fbPixelsMutation.isPending}
                    onClick={() => fbPixelsMutation.mutate()}
                  >
                    {fbPixelsMutation.isPending ? 'Saving…' : 'Save pixels'}
                  </Button>
                </div>
                {fbPixelsMutation.isError && (
                  <p className="text-sm text-destructive">{fbPixelsMutation.error.message}</p>
                )}
              </>
            )}
          </CardContent>
        </Card>
      )}

      {showChannelConfig && (
        <Card>
          <CardHeader>
            <CardTitle>PropellerAds S2S</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {!isAdmin ? (
              <p className="font-mono text-sm">
                {channelSettings?.pa?.postback_url || 'Not configured'}
              </p>
            ) : (
              <>
                <div className="space-y-2">
                  <label className="text-sm text-muted-foreground">Postback URL</label>
                  <Input
                    value={paS2s.postback_url}
                    onChange={(e) => setPaS2s({ ...paS2s, postback_url: e.target.value })}
                    placeholder="http://ad.propellerads.com/conversion.php"
                  />
                </div>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <label className="text-sm text-muted-foreground">AID</label>
                    <Input
                      value={paS2s.aid ?? ''}
                      onChange={(e) => setPaS2s({ ...paS2s, aid: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm text-muted-foreground">TID</label>
                    <Input
                      value={paS2s.tid ?? ''}
                      onChange={(e) => setPaS2s({ ...paS2s, tid: e.target.value })}
                    />
                  </div>
                </div>
                <Button
                  type="button"
                  disabled={paS2sMutation.isPending}
                  onClick={() => paS2sMutation.mutate()}
                >
                  {paS2sMutation.isPending ? 'Saving…' : 'Save PropellerAds settings'}
                </Button>
                {paS2sMutation.isError && (
                  <p className="text-sm text-destructive">{paS2sMutation.error.message}</p>
                )}
              </>
            )}
          </CardContent>
        </Card>
      )}

      {showChannelConfig && (
        <Card>
          <CardHeader>
            <CardTitle>TrafficStars S2S</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {!isAdmin ? (
              <p className="font-mono text-sm">
                {channelSettings?.ts?.postback_url || 'Not configured'}
              </p>
            ) : (
              <>
                <div className="space-y-2">
                  <label className="text-sm text-muted-foreground">Postback URL</label>
                  <Input
                    value={tsS2s.postback_url}
                    onChange={(e) => setTsS2s({ ...tsS2s, postback_url: e.target.value })}
                    placeholder="https://tsyndicate.com/api/v1/cpa/action"
                  />
                </div>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <label className="text-sm text-muted-foreground">API key</label>
                    <Input
                      type="password"
                      value={tsS2s.api_key === '***' ? '' : (tsS2s.api_key ?? '')}
                      onChange={(e) => setTsS2s({ ...tsS2s, api_key: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm text-muted-foreground">Goal ID</label>
                    <Input
                      value={tsS2s.goal_id ?? ''}
                      onChange={(e) => setTsS2s({ ...tsS2s, goal_id: e.target.value })}
                    />
                  </div>
                </div>
                <Button
                  type="button"
                  disabled={tsS2sMutation.isPending}
                  onClick={() => tsS2sMutation.mutate()}
                >
                  {tsS2sMutation.isPending ? 'Saving…' : 'Save TrafficStars settings'}
                </Button>
                {tsS2sMutation.isError && (
                  <p className="text-sm text-destructive">{tsS2sMutation.error.message}</p>
                )}
              </>
            )}
          </CardContent>
        </Card>
      )}

      <Dialog
        open={!!gateDialog}
        onOpenChange={(open) => {
          if (!open) {
            setGateDialog(null)
            setConfirmLive(false)
          }
        }}
        title="Dry-run safety gate"
        description="Enabling live dispatch requires explicit confirmation."
        footer={
          <>
            <Button
              variant="outline"
              onClick={() => {
                setGateDialog(null)
                setConfirmLive(false)
              }}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              disabled={!confirmLive || patchMutation.isPending}
              onClick={() => {
                if (gateDialog) {
                  patchMutation.mutate({
                    brandId: gateDialog.brandId,
                    channel: gateDialog.channel,
                    enabled: gateDialog.enabled,
                    confirmLive: true,
                  })
                }
              }}
            >
              {patchMutation.isPending ? 'Confirming…' : 'Confirm live dispatch'}
            </Button>
          </>
        }
      >
        {gateDialog && (
          <div className="space-y-4 text-sm">
            <p>{gateDialog.message}</p>
            <p className="text-muted-foreground">
              The deployment must allow live dispatch for this change to succeed. If dry-run mode
              is enforced at the deployment level, confirmation will still return a 409 until
              cutover is complete.
            </p>
            <label className="flex items-start gap-2">
              <input
                type="checkbox"
                checked={confirmLive}
                onChange={(e) => setConfirmLive(e.target.checked)}
                className="mt-1"
              />
              <span>
                I understand this will enable live dispatch to{' '}
                <strong>{gateDialog.channel}</strong> and accept responsibility for production
                traffic.
              </span>
            </label>
          </div>
        )}
      </Dialog>
    </div>
  )
}
